// src/api/contents.ts
import { API_BASE, getAuthHeaders } from './config';
export type ContentLanguageCode = 'PL' | 'EN' | 'ES' | 'DE' | 'UK' | 'IT' | 'FR';

/** ---------- CONTENTS ---------- */

export const createContent = async (form: any) => {
    const res = await fetch(`${API_BASE}/contents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
            ...form,
            read_time_minutes: Number(form.read_time_minutes) || null,
            is_visible: !!form.is_visible,
            author_ids: form.author_ids ?? [],
            tag_ids: form.tag_ids ?? [],
            difficulty_level: form.difficulty_level || null,
        }),
    });
    if (!res.ok) throw await res.json();
    return res.json();
};

export const updateContent = async (id: string | number, form: any) => {
    const res = await fetch(`${API_BASE}/contents/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
            ...form,
            read_time_minutes: Number(form.read_time_minutes) || null,
            is_visible: !!form.is_visible,
            author_ids: form.author_ids ?? [],
            difficulty_level: form.difficulty_level || null,
        }),
    });
    if (!res.ok) throw await res.json();
    return res.json();
};

export const deleteContent = async (contentId: string | number) => {
    const res = await fetch(`${API_BASE}/contents/${contentId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
    });
    if (!res.ok) throw await res.json();
    return res.json();
};

export const getContentById = async (contentId: number | string) => {
    const res = await fetch(`${API_BASE}/contents/${contentId}`, { headers: getAuthHeaders() });
    if (!res.ok) throw await res.json();
    return res.json();
};

export const getAllContentsDetails = async () => {
    const res = await fetch(`${API_BASE}/contents/details`, { headers: getAuthHeaders() });
    if (!res.ok) throw await res.json();
    return res.json();
};

export const assignTagsToContent = async (contentId: string | number, tagIds: number[]) => {
    const res = await fetch(`${API_BASE}/contents/${contentId}/tags`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ tag_ids: tagIds }),
    });
    if (!res.ok) throw await res.json();
    return res.json();
};

/** ---------- MEDIA: COVER ---------- */

export const uploadCover = async (contentId: string | number, file: File) => {
    const formData = new FormData();
    formData.append('image', file);
    const res = await fetch(`${API_BASE}/contents/${contentId}/upload-image`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData,
    });
    if (!res.ok) throw await res.json();
    return res.json();
};

export const deleteCover = async (contentId: string | number, filename: string) => {
    const res = await fetch(`${API_BASE}/contents/${contentId}/image/${encodeURIComponent(filename)}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
    });
    if (!res.ok) throw await res.json();
    return res.json();
};

/** ---------- MEDIA: AUDIOS (NOWE V2) ---------- */

export type AudioMeta = {
    fileName?: string;                 // wymagane dla UPLOAD
    lang: ContentLanguageCode;
    narrator: string;                  // WYMAGANE dla UPLOAD (backend wymusza)
    narrator_id?: number | null;
    duration?: number | null;
    source_url?: string | null;        // dla LINK
    license?: string | null;
    storage?: 'UPLOAD' | 'LINK';       // domyślnie wyliczane po stronie serwera
    is_visible?: boolean;              // opcjonalnie
};

export const addAudios = async (
    contentId: string | number,
    files: File[],
    metas: AudioMeta[]
) => {
    const fd = new FormData();
    fd.append('meta', JSON.stringify(metas));
    files.forEach(f => fd.append('audio', f));

    const res = await fetch(`${API_BASE}/contents/${contentId}/audios`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: fd,
    });
    if (!res.ok) throw await res.json();
    return res.json();
};

export const addAudioLinks = async (
    contentId: string | number,
    metas: AudioMeta[],
) => {
    const fd = new FormData();
    fd.append('meta', JSON.stringify(metas));
    const res = await fetch(`${API_BASE}/contents/${contentId}/audios`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: fd,
    });
    if (!res.ok) throw await res.json();
    return res.json();
};

export const listAudios = async (contentId: string | number, lang?: string) => {
    const url = `${API_BASE}/contents/${contentId}/audios${lang ? `?lang=${encodeURIComponent(lang)}` : ''}`;
    const res = await fetch(url, { headers: getAuthHeaders() });
    if (!res.ok) throw await res.json();
    return res.json();
};

export const updateAudio = async (
    contentId: string | number,
    audioId: number,
    patch: Partial<{
        language_code: ContentLanguageCode;
        narrator_name: string | null;
        narrator_id: number | null;
        license: string | null;
        source_url: string | null;
        is_visible: boolean;
    }>
) => {
    const res = await fetch(`${API_BASE}/contents/${contentId}/audios/${audioId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(patch),
    });
    if (!res.ok) throw await res.json();
    return res.json();
};

export const deleteAudio = async (contentId: string | number, audioId: number) => {
    const res = await fetch(`${API_BASE}/contents/${contentId}/audios/${audioId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
    });
    if (!res.ok) throw await res.json();
    return res.json();
};

/** ---------- TRANSLATIONS ---------- */

export const getTranslations = async (contentId: string | number) => {
    const res = await fetch(`${API_BASE}/contents/${contentId}/translations`, { headers: getAuthHeaders() });
    if (!res.ok) throw await res.json();
    return res.json();
};

export const upsertTranslation = async (
    contentId: string | number,
    payload: {
        language_code: ContentLanguageCode;
        translated_text?: string | null;
        translated_description?: string | null;
        translator_id?: number | null;
        is_visible?: boolean;
    }
) => {
    const res = await fetch(`${API_BASE}/contents/${contentId}/translations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(payload),
    });
    if (!res.ok) throw await res.json();
    return res.json();
};

export const patchTranslation = async (
    contentId: string | number,
    lang: ContentLanguageCode,
    payload: Partial<{
        translated_text: string | null;
        translated_description: string | null;
        translator_id: number | null;
        is_visible: boolean;
    }>
) => {
    const res = await fetch(`${API_BASE}/contents/${contentId}/translations/${lang}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(payload),
    });
    if (!res.ok) throw await res.json();
    return res.json();
};

export const deleteTranslation = async (contentId: string | number, lang: ContentLanguageCode) => {
    const res = await fetch(`${API_BASE}/contents/${contentId}/translations/${lang}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
    });
    if (!res.ok) throw await res.json();
    return res.json();
};
